import React, { useEffect, useState, useCallback } from 'react'
import { supabase } from '../lib/supabase'
import PageHeader from '../components/PageHeader'
import TableFilters from '../components/TableFilters'
import AddServiceModal from '../components/AddServiceModal'

function fmt(n) { return '₹' + Number(n).toLocaleString('en-IN') }

function renewalBadge(dateStr) {
  if (!dateStr) return null
  const today = new Date(); today.setHours(0,0,0,0)
  const d = new Date(dateStr + 'T00:00:00')
  const diff = Math.round((d - today) / (1000 * 60 * 60 * 24))
  if (diff < 0) return { text: `Expired ${Math.abs(diff)}d ago`, cls: 'badge badge-red' }
  if (diff <= 7) return { text: `Expires in ${diff}d`, cls: 'badge badge-red' }
  if (diff <= 30) return { text: `Expires in ${diff}d`, cls: 'badge badge-amber' }
  return { text: dateStr, cls: 'badge badge-gray' }
}

function payBadge(s) {
  const map = { Received: 'badge-green', Pending: 'badge-amber', Overdue: 'badge-red', Waived: 'badge-gray' }
  return 'badge ' + (map[s] || 'badge-gray')
}

function statusBadge(s) {
  const map = { Running: 'badge-green', 'Not Started': 'badge-gray', Paused: 'badge-amber', Stopped: 'badge-red' }
  return 'badge ' + (map[s] || 'badge-gray')
}

const FILTERS = [
  { key: 'search', label: 'Search', type: 'text', fields: ['client_name', 'brand_name', 'domain', 'hosting_provider'] },
  { key: 'status', label: 'Status', type: 'select', options: ['Not Started', 'Running', 'Paused', 'Stopped'] },
]

// Archive modal
function ArchiveModal({ service, onClose, onArchived }) {
  const [status, setStatus] = useState('Paused')
  const [reason, setReason] = useState('')
  const [saving, setSaving] = useState(false)

  async function doArchive() {
    setSaving(true)
    await supabase.from('services').update({ status }).eq('id', service.id)
    onArchived(service.id, status)
    setSaving(false)
    onClose()
  }

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 420 }}>
        <div className="modal-header">
          <h3>Pause / Stop Service</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <div style={{ background: 'var(--surface2)', borderRadius: 'var(--radius)', padding: 12, marginBottom: 16, fontSize: 13 }}>
            <strong>{service.client_name}</strong> — {service.brand_name}
            {service.domain && <div style={{ color: 'var(--text2)', marginTop: 4, fontSize: 12 }}>{service.domain}</div>}
          </div>
          <div className="form-field">
            <label>Status</label>
            <select value={status} onChange={e => setStatus(e.target.value)}>
              <option>Paused</option>
              <option>Stopped</option>
            </select>
          </div>
          <div className="form-field">
            <label>Reason (optional)</label>
            <textarea rows={2} value={reason} onChange={e => setReason(e.target.value)} style={{ resize: 'vertical' }} />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={doArchive} disabled={saving}>
            {saving ? 'Saving…' : status === 'Paused' ? 'Pause' : 'Stop'}
          </button>
        </div>
      </div>
    </div>
  )
}

export default function Hosting() {
  const [services, setServices] = useState([])
  const [clientsMap, setClientsMap] = useState({})
  const [brandsMap, setBrandsMap] = useState({})
  const [filtered, setFiltered] = useState([])

  const [addModal, setAddModal] = useState(false)
  const [editService, setEditService] = useState(null)
  const [archiveService, setArchiveService] = useState(null)

  const handleFiltered = useCallback(d => setFiltered(d), [])

  useEffect(() => {
    async function load() {
      const [sRes, cRes, bRes] = await Promise.all([
        supabase.from('services')
          .select('*')
          .eq('service_type', 'Hosting')
          .not('status', 'in', '("Paused","Stopped")')
          .order('created_at', { ascending: false }),
        supabase.from('clients').select('id, name'),
        supabase.from('brands').select('id, name, client_id'),
      ])
      const cm = {}; (cRes.data || []).forEach(c => { cm[c.id] = c.name })
      const bm = {}; (bRes.data || []).forEach(b => { bm[b.id] = b.name })
      setClientsMap(cm)
      setBrandsMap(bm)
      setServices(enrich(sRes.data || [], cm, bm))
    }
    load()
  }, [])

  function enrich(rows, cm, bm) {
    return rows.map(s => ({
      ...s,
      client_name: cm[s.client_id] || '—',
      brand_name: bm[s.brand_id] || '—',
    }))
  }

  function onSaved(service) {
    const enriched = { ...service, client_name: clientsMap[service.client_id] || '—', brand_name: brandsMap[service.brand_id] || '—' }
    if (editService) {
      setServices(prev => prev.map(s => s.id === service.id ? enriched : s))
    } else {
      // Refresh clientsMap/brandsMap in case a new client/brand was added
      supabase.from('clients').select('id, name').then(({ data }) => {
        const cm = {}; (data || []).forEach(c => { cm[c.id] = c.name })
        setClientsMap(cm)
        supabase.from('brands').select('id, name').then(({ data: bData }) => {
          const bm = {}; (bData || []).forEach(b => { bm[b.id] = b.name })
          setBrandsMap(bm)
          setServices(prev => [...prev, { ...service, client_name: cm[service.client_id] || '—', brand_name: bm[service.brand_id] || '—' }])
        })
      })
    }
    setAddModal(false)
    setEditService(null)
  }

  function onArchived(id, newStatus) {
    setServices(prev => prev.filter(s => s.id !== id))
  }

  async function remove(id) {
    if (!window.confirm('Delete this hosting service?')) return
    await supabase.from('services').delete().eq('id', id)
    setServices(prev => prev.filter(s => s.id !== id))
  }

  async function markPaid(id) {
    const svc = services.find(s => s.id === id)
    if (!svc) return
    const today = new Date().toISOString().slice(0, 10)
    // Log billing record
    await supabase.from('billing_records').insert([{
      client_id: svc.client_id,
      brand_id: svc.brand_id,
      project_table: 'services',
      project_id: id,
      amount: Number(svc.value) || 0,
      billing_date: today,
      due_date: svc.next_billing_date || today,
      status: 'Paid',
      paid_date: today,
      description: svc.brand_name + ' — Hosting payment received',
    }]).select()
    // Advance next billing date
    let updates = { payment_status: 'Received' }
    if (svc.next_billing_date) {
      const cur = new Date(svc.next_billing_date + 'T00:00:00')
      const cycle = svc.billing_cycle || 'Yearly'
      if (cycle === 'Monthly') cur.setMonth(cur.getMonth() + 1)
      else if (cycle === 'Quarterly') cur.setMonth(cur.getMonth() + 3)
      else if (cycle === 'Half Yearly') cur.setMonth(cur.getMonth() + 6)
      else cur.setFullYear(cur.getFullYear() + 1)
      updates = { payment_status: 'Pending', next_billing_date: cur.toISOString().slice(0, 10) }
    }
    await supabase.from('services').update(updates).eq('id', id)
    setServices(prev => prev.map(s => s.id === id ? { ...s, ...updates } : s))
  }

  const totalValue = services.reduce((s, r) => s + (Number(r.value) || 0), 0)

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
      <PageHeader
        title="Active Projects — Hosting"
        subtitle={`${services.length} services · ${fmt(totalValue)} total`}
        action={<button className="btn btn-primary" onClick={() => setAddModal(true)}>+ Add hosting</button>}
      />
      <div style={{ padding: 28, flex: 1, overflowX: 'auto' }}>
        <TableFilters filters={FILTERS} data={filtered.length > 0 || services.length === 0 ? filtered : services} onFiltered={handleFiltered} />
        <div className="card" style={{ overflowX: 'auto' }}>
          <table style={{ minWidth: 1000 }}>
            <thead>
              <tr>
                <th>Client</th>
                <th>Brand / Project</th>
                <th>Domain</th>
                <th>Renewal date</th>
                <th>Provider</th>
                <th>Plan</th>
                <th>Value</th>
                <th>Billing period</th>
                <th>Next billing</th>
                <th>Payment</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(s => {
                const renewal = renewalBadge(s.domain_renewal_date)
                return (
                  <tr key={s.id}>
                    <td style={{ fontWeight: 500 }}>{s.client_name}</td>
                    <td>
                      <div>{s.brand_name}</div>
                      {s.remarks && <div style={{ color: 'var(--text3)', fontSize: 11, marginTop: 2 }}>{s.remarks}</div>}
                    </td>
                    <td style={{ fontFamily: 'var(--mono)', fontSize: 12 }}>{s.domain || '—'}</td>
                    <td>
                      {renewal
                        ? <span className={renewal.cls}>{renewal.text}</span>
                        : <span style={{ color: 'var(--text3)' }}>—</span>}
                    </td>
                    <td style={{ color: 'var(--text2)' }}>{s.hosting_provider || '—'}</td>
                    <td style={{ color: 'var(--text2)' }}>{s.hosting_plan || '—'}</td>
                    <td style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 500 }}>{fmt(s.value)}</td>
                    <td><span className="badge badge-gray">{s.billing_cycle || '—'}</span></td>
                    <td style={{ color: 'var(--text2)' }}>{s.next_billing_date || '—'}</td>
                    <td>
                      {s.payment_status === 'Received'
                        ? <span className="badge badge-green">Paid</span>
                        : <button className="badge badge-amber" style={{ cursor: 'pointer', border: 'none' }} onClick={() => markPaid(s.id)}>Mark Paid</button>
                      }
                    </td>
                    <td><span className={statusBadge(s.status)}>{s.status}</span></td>
                    <td>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button className="btn" style={{ padding: '4px 10px', fontSize: 12 }} onClick={() => setEditService(s)}>Edit</button>
                        <button className="btn" style={{ padding: '4px 10px', fontSize: 12, color: 'var(--warning)', borderColor: 'var(--warning)' }} onClick={() => setArchiveService(s)}>Pause</button>
                        <button className="btn btn-danger" style={{ padding: '4px 10px', fontSize: 12 }} onClick={() => remove(s.id)}>Del</button>
                      </div>
                    </td>
                  </tr>
                )
              })}
              {filtered.length === 0 && (
                <tr><td colSpan={12} style={{ textAlign: 'center', padding: 40, color: 'var(--text3)' }}>No hosting services yet</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {addModal && (
        <AddServiceModal
          lockedServiceType="Hosting"
          onClose={() => setAddModal(false)}
          onSaved={onSaved}
        />
      )}

      {editService && (
        <AddServiceModal
          lockedServiceType="Hosting"
          editService={editService}
          onClose={() => setEditService(null)}
          onSaved={onSaved}
        />
      )}

      {archiveService && (
        <ArchiveModal
          service={archiveService}
          onClose={() => setArchiveService(null)}
          onArchived={onArchived}
        />
      )}
    </div>
  )
}
